import { Dispatch, SetStateAction, useCallback } from 'react';
import { MaintenanceDraftWithDone } from '../types';
import { MaintenanceIntervalType } from '../../../../types/Enums';
import { formatDateToString, MaintenanceActivity } from '../../../../Helpers/maintenanceCostUtils';
import { formatDateForStorage } from '../../../../Helpers/DateUtils';
import { Building } from '../../../../types/protocols';
import { ComponentLifetime, MaintenanceTemplate, TemplatesByType } from '../../TemplatesEditor/types';
import { activityDefaults } from '../utils/draftSync';
import { Attachment } from '../../../../types/attachments';
import { addActivityToEntry, removeActivityFromEntry } from '../utils/removedActivities';
import { withActivityComment } from '../utils/activityComment';
import { CostSettings, applyActivityCostSettings, applyReplacementCostSettings, droppedAttachments } from '../utils/costSettings';
import { tombstoneUnreferencedCostAttachments } from '../../../../api/maintenanceDraftApi';

interface UseMaintenanceHandlersProps {
    maintenanceDraft: MaintenanceDraftWithDone;
    setMaintenanceDraft: Dispatch<SetStateAction<MaintenanceDraftWithDone>>;
    scheduleAutosave: (draft?: MaintenanceDraftWithDone) => void;
    building: Building | null;
    componentLifetimes: Record<string, ComponentLifetime>;
    maintenanceTemplates: TemplatesByType;
    /** Every template by id, for rows on templates of another type */
    getTemplateById: (templateId: string) => MaintenanceTemplate | null;
}

interface UseMaintenanceHandlersReturn {
    handleReplacementYearChange: (componentKey: string, newYear: string) => void;
    handleReplacementYearBlur: (componentKey: string) => void;
    handleReplacementCostChange: (componentKey: string, value: string) => void;
    handleReplacementCostBlur: (componentKey: string) => void;
    handleMaintenanceDateChange: (componentKey: string, templateId: string, newDate: string) => void;
    handleMaintenanceDateBlur: (componentKey: string, templateId: string) => void;
    handleMaintenanceIntervalChange: (componentKey: string, templateId: string, newInterval: string) => void;
    handleMaintenanceIntervalBlur: (componentKey: string, templateId: string) => void;
    handleMaintenanceIntervalTypeChange: (componentKey: string, templateId: string, newType: MaintenanceIntervalType) => void;
    handleMaintenanceCostChange: (componentKey: string, templateId: string, value: string) => void;
    handleMaintenanceCostBlur: (componentKey: string, templateId: string) => void;
    handleMaintenanceActivityRemove: (componentKey: string, templateId: string, isTypeTemplate: boolean) => void;
    handleMaintenanceActivityAdd: (componentKey: string, templateId: string) => void;
    handleDoneToggle: (componentKey: string) => void;
    handleExcludeToggle: (componentKey: string) => void;

    /** Applies what the cost modal saved: source, cost or unit price, comment, attachments, investment share */
    /** The same settings on every component's row (several in Massredigering), in one draft update */
    handleActivityCostSettingsSave: (componentKeys: string[], templateId: string, settings: CostSettings) => void;
    handleReplacementCostSettingsSave: (componentKeys: string[], settings: CostSettings) => void;
    /** Sets the row's free-text comment; an empty text removes it */
    handleActivityCommentChange: (componentKey: string, templateId: string, text: string) => void;

    // Custom quantity handlers (area/length override for cost calculation)
    handleActivityQuantityChange: (componentKey: string, templateId: string, quantity: number) => void;
    handleActivityQuantityClear: (componentKey: string, templateId: string) => void;
    // Antal for a per_unit activity (cost multiplier; 1 or a clear removes it)
    handleActivityUnitCountChange: (componentKey: string, templateId: string, count: number) => void;
    handleActivityUnitCountClear: (componentKey: string, templateId: string) => void;
    handleReplacementUnitCountChange: (componentKey: string, count: number) => void;
    handleReplacementUnitCountClear: (componentKey: string) => void;


    // Bulk apply handler
    handleBulkApply: (sourceKey: string, targetKeys: string[]) => void;
    handleReplaceActivities: (
        componentKey: string,
        activities: { [templateId: string]: MaintenanceActivity },
        removedActivities: { [templateId: string]: true } | undefined
    ) => void;
}

export const useMaintenanceHandlers = ({
    maintenanceDraft,
    setMaintenanceDraft,
    scheduleAutosave,
    building,
    componentLifetimes,
    maintenanceTemplates,
    getTemplateById
}: UseMaintenanceHandlersProps): UseMaintenanceHandlersReturn => {

    // What a row without its own date or interval takes: the template's, for the component's direction
    const defaultsFor = useCallback((componentKey: string, templateId: string): MaintenanceActivity | null =>
        building ? activityDefaults(building, componentKey, templateId, componentLifetimes, maintenanceTemplates, getTemplateById) : null,
    [building, componentLifetimes, maintenanceTemplates, getTemplateById]);

    const handleReplacementYearChange = useCallback((componentKey: string, newYear: string) => {
        const year = parseInt(newYear, 10);
        setMaintenanceDraft(prev => ({
            ...prev,
            components: {
                ...prev.components,
                [componentKey]: {
                    ...prev.components[componentKey],
                    replacementYear: isNaN(year) ? undefined : year,
                    maintenanceActivities: prev.components[componentKey]?.maintenanceActivities || {}
                }
            }
        }));
    }, [setMaintenanceDraft]);

    const handleReplacementYearBlur = useCallback((componentKey: string) => {
        // Validate year - revert to current year if in the past
        const currentYear = new Date().getFullYear();

        setMaintenanceDraft(prev => {
            const replacementYear = prev.components[componentKey]?.replacementYear;
            if (replacementYear === undefined || replacementYear === null) return prev;

            // If year is in the past, revert to current year
            if (replacementYear < currentYear) {
                return {
                    ...prev,
                    components: {
                        ...prev.components,
                        [componentKey]: {
                            ...prev.components[componentKey],
                            replacementYear: currentYear,
                            maintenanceActivities: prev.components[componentKey]?.maintenanceActivities || {}
                        }
                    }
                };
            }

            return prev;
        });

        scheduleAutosave();
    }, [setMaintenanceDraft, scheduleAutosave]);

    const handleMaintenanceDateChange = useCallback((componentKey: string, templateId: string, newDate: string) => {
        setMaintenanceDraft(prev => {
            const existingActivity = prev.components[componentKey]?.maintenanceActivities?.[templateId];

            let intervalType = existingActivity?.intervalType;
            let intervalValue = existingActivity?.intervalValue;

            if (!intervalType || intervalValue === undefined) {
                const defaults = defaultsFor(componentKey, templateId);
                intervalType = defaults?.intervalType ?? MaintenanceIntervalType.years;
                intervalValue = defaults?.intervalValue ?? 1;
            }

            return {
                ...prev,
                components: {
                    ...prev.components,
                    [componentKey]: {
                        ...prev.components[componentKey],
                        replacementYear: prev.components[componentKey]?.replacementYear,
                        maintenanceActivities: {
                            ...prev.components[componentKey]?.maintenanceActivities,
                            [templateId]: {
                                ...existingActivity,
                                nextDate: newDate || formatDateToString(new Date()),
                                intervalType: intervalType,
                                intervalValue: intervalValue,
                                lastUpdated: formatDateForStorage(new Date())
                            }
                        }
                    }
                }
            };
        });
    }, [setMaintenanceDraft, defaultsFor]);

    const handleMaintenanceDateBlur = useCallback((componentKey: string, templateId: string) => {
        // Validate date - revert to today/current year if in the past
        setMaintenanceDraft(prev => {
            const activity = prev.components[componentKey]?.maintenanceActivities?.[templateId];
            if (!activity?.nextDate) return prev;

            const enteredDate = new Date(activity.nextDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const currentYear = today.getFullYear();

            // For yearly intervals, only validate the year
            if (activity.intervalType === MaintenanceIntervalType.years) {
                const enteredYear = enteredDate.getFullYear();
                if (isNaN(enteredYear) || enteredYear < currentYear) {
                    // Revert to January 1st of current year
                    const currentYearDate = new Date(currentYear, 0, 1);
                    return {
                        ...prev,
                        components: {
                            ...prev.components,
                            [componentKey]: {
                                ...prev.components[componentKey],
                                maintenanceActivities: {
                                    ...prev.components[componentKey]?.maintenanceActivities,
                                    [templateId]: {
                                        ...activity,
                                        nextDate: formatDateToString(currentYearDate),
                                        lastUpdated: formatDateForStorage(new Date())
                                    }
                                }
                            }
                        }
                    };
                }
            } else {
                // For non-yearly intervals, validate the full date
                if (isNaN(enteredDate.getTime()) || enteredDate < today) {
                    return {
                        ...prev,
                        components: {
                            ...prev.components,
                            [componentKey]: {
                                ...prev.components[componentKey],
                                maintenanceActivities: {
                                    ...prev.components[componentKey]?.maintenanceActivities,
                                    [templateId]: {
                                        ...activity,
                                        nextDate: formatDateToString(today),
                                        lastUpdated: formatDateForStorage(new Date())
                                    }
                                }
                            }
                        }
                    };
                }
            }

            return prev;
        });

        scheduleAutosave();
    }, [setMaintenanceDraft, scheduleAutosave]);

    const handleMaintenanceIntervalChange = useCallback((componentKey: string, templateId: string, newInterval: string) => {
        const interval = parseInt(newInterval, 10);

        setMaintenanceDraft(prev => {
            const existingActivity = prev.components[componentKey]?.maintenanceActivities?.[templateId];
            const intervalType = existingActivity?.intervalType
                ?? defaultsFor(componentKey, templateId)?.intervalType
                ?? MaintenanceIntervalType.years;

            return {
                ...prev,
                components: {
                    ...prev.components,
                    [componentKey]: {
                        ...prev.components[componentKey],
                        replacementYear: prev.components[componentKey]?.replacementYear,
                        maintenanceActivities: {
                            ...prev.components[componentKey]?.maintenanceActivities,
                            [templateId]: {
                                ...existingActivity,
                                nextDate: existingActivity?.nextDate || formatDateToString(new Date()),
                                intervalType: intervalType,
                                intervalValue: isNaN(interval) ? 1 : interval,
                                lastUpdated: formatDateForStorage(new Date())
                            }
                        }
                    }
                }
            };
        });
    }, [setMaintenanceDraft, defaultsFor]);

    const handleMaintenanceIntervalBlur = useCallback((_componentKey: string, _templateId: string) => {
        scheduleAutosave();
    }, [scheduleAutosave]);

    const handleMaintenanceIntervalTypeChange = useCallback((componentKey: string, templateId: string, newType: MaintenanceIntervalType) => {
        setMaintenanceDraft(prev => ({
            ...prev,
            components: {
                ...prev.components,
                [componentKey]: {
                    ...prev.components[componentKey],
                    replacementYear: prev.components[componentKey]?.replacementYear,
                    maintenanceActivities: {
                        ...prev.components[componentKey]?.maintenanceActivities,
                        [templateId]: {
                            ...prev.components[componentKey]?.maintenanceActivities?.[templateId],
                            nextDate: prev.components[componentKey]?.maintenanceActivities?.[templateId]?.nextDate || formatDateToString(new Date()),
                            intervalType: newType,
                            intervalValue: prev.components[componentKey]?.maintenanceActivities?.[templateId]?.intervalValue
                                || defaultsFor(componentKey, templateId)?.intervalValue
                                || 1,
                            lastUpdated: formatDateForStorage(new Date())
                        }
                    }
                }
            }
        }));
        scheduleAutosave();
    }, [setMaintenanceDraft, scheduleAutosave, defaultsFor]);

    const handleReplacementCostChange = useCallback((componentKey: string, value: string): void => {
        const cost = value === '' ? undefined : parseFloat(value);

        setMaintenanceDraft(prev => ({
            ...prev,
            components: {
                ...prev.components,
                [componentKey]: {
                    ...prev.components[componentKey],
                    replacementCost: cost
                }
            }
        }));
    }, [setMaintenanceDraft]);

    const handleReplacementCostBlur = useCallback((_componentKey: string) => {
        scheduleAutosave();
    }, [scheduleAutosave]);

    const handleMaintenanceCostChange = useCallback((componentKey: string, templateId: string, value: string): void => {
        const cost = value === '' ? undefined : parseFloat(value);

        setMaintenanceDraft(prev => {
            const existingActivity = prev.components[componentKey]?.maintenanceActivities?.[templateId];

            // A row without its own date or interval (the user edits the cost first) takes the template's
            let intervalType = existingActivity?.intervalType;
            let intervalValue = existingActivity?.intervalValue;
            let nextDate = existingActivity?.nextDate;

            if (!intervalType || intervalValue === undefined || !nextDate) {
                const defaults = defaultsFor(componentKey, templateId);
                if (!intervalType || intervalValue === undefined) {
                    intervalType = defaults?.intervalType ?? MaintenanceIntervalType.years;
                    intervalValue = defaults?.intervalValue ?? 1;
                }
                if (!nextDate) nextDate = defaults?.nextDate ?? formatDateToString(new Date());
            }

            return {
                ...prev,
                components: {
                    ...prev.components,
                    [componentKey]: {
                        ...prev.components[componentKey],
                        maintenanceActivities: {
                            ...prev.components[componentKey]?.maintenanceActivities,
                            [templateId]: {
                                ...existingActivity,
                                cost: cost,
                                intervalType: intervalType!,
                                intervalValue: intervalValue!,
                                nextDate: nextDate!,
                                lastUpdated: formatDateForStorage(new Date())
                            }
                        }
                    }
                }
            };
        });
    }, [setMaintenanceDraft, defaultsFor]);

    const handleMaintenanceCostBlur = useCallback((_componentKey: string, _templateId: string) => {
        scheduleAutosave();
    }, [scheduleAutosave]);

    // Removes the row from the component; a type row leaves a marker so it is not re-created
    const handleMaintenanceActivityRemove = useCallback((componentKey: string, templateId: string, isTypeTemplate: boolean): void => {
        setMaintenanceDraft(prev => {
            const entry = prev.components[componentKey];
            if (!entry) return prev;

            const updatedDraft = {
                ...prev,
                components: {
                    ...prev.components,
                    [componentKey]: removeActivityFromEntry(entry, templateId, isTypeTemplate)
                }
            };

            scheduleAutosave(updatedDraft);

            return updatedDraft;
        });
    }, [setMaintenanceDraft, scheduleAutosave]);

    // The template search picked a template: the row is on the component
    const handleMaintenanceActivityAdd = useCallback((componentKey: string, templateId: string): void => {
        setMaintenanceDraft(prev => {
            const updatedDraft = {
                ...prev,
                components: {
                    ...prev.components,
                    [componentKey]: addActivityToEntry(
                        prev.components[componentKey] ?? { maintenanceActivities: {} },
                        templateId,
                        defaultsFor(componentKey, templateId) ?? { lastUpdated: formatDateForStorage(new Date()) } as MaintenanceActivity
                    )
                }
            };

            scheduleAutosave(updatedDraft);

            return updatedDraft;
        });
    }, [setMaintenanceDraft, scheduleAutosave, defaultsFor]);

    const handleDoneToggle = useCallback((componentKey: string): void => {
        setMaintenanceDraft(prev => {
            const currentComponent = prev.components[componentKey] || {
                maintenanceActivities: {},
                isDone: false
            };
            const newIsDone = !currentComponent.isDone;

            const updatedDraft = {
                ...prev,
                components: {
                    ...prev.components,
                    [componentKey]: {
                        ...currentComponent,
                        isDone: newIsDone
                    }
                }
            };

            scheduleAutosave(updatedDraft);

            return updatedDraft;
        });
    }, [setMaintenanceDraft, scheduleAutosave]);

    const handleExcludeToggle = useCallback((componentKey: string): void => {
        setMaintenanceDraft(prev => {
            const currentComponent = prev.components[componentKey] || {
                maintenanceActivities: {},
                excluded: false
            };
            const newExcluded = !currentComponent.excluded;

            const updatedDraft = {
                ...prev,
                components: {
                    ...prev.components,
                    [componentKey]: {
                        ...currentComponent,
                        excluded: newExcluded
                    }
                }
            };

            scheduleAutosave(updatedDraft);

            return updatedDraft;
        });
    }, [setMaintenanceDraft, scheduleAutosave]);

    // === Cost settings (the cost modal saves everything in one update) ===

    // Attachments the save dropped are tombstoned when nothing else holds them:
    // copies share attachments, and the active plan and archives keep theirs.
    const tombstoneDropped = useCallback((
        entityType: string,
        dropped: { [key: string]: Attachment },
        draftAfter: MaintenanceDraftWithDone
    ): void => {
        const buildingKey = draftAfter.metadata?.buildingKey || building?.key;
        if (!buildingKey || Object.keys(dropped).length === 0) return;
        void tombstoneUnreferencedCostAttachments(buildingKey, entityType, dropped, draftAfter);
    }, [building]);

    // The rows of every component get the settings in one update, so an attachment dropped from
    // all of them at once is seen as unreferenced by the one tombstone pass
    const handleActivityCostSettingsSave = useCallback((
        componentKeys: string[],
        templateId: string,
        settings: CostSettings
    ): void => {
        const keys = componentKeys.filter(key => maintenanceDraft.components[key]?.maintenanceActivities?.[templateId] !== undefined);
        if (keys.length === 0) return;

        const lastUpdated = formatDateForStorage(new Date());
        const withSettings = (draft: MaintenanceDraftWithDone): MaintenanceDraftWithDone => {
            let updated = draft;
            for (const componentKey of keys) {
                const current = updated.components[componentKey]?.maintenanceActivities?.[templateId];
                if (!current) continue;
                updated = {
                    ...updated,
                    components: {
                        ...updated.components,
                        [componentKey]: {
                            ...updated.components[componentKey],
                            maintenanceActivities: {
                                ...updated.components[componentKey]?.maintenanceActivities,
                                [templateId]: applyActivityCostSettings(current, settings, lastUpdated)
                            }
                        }
                    }
                };
            }
            return updated;
        };

        const draftAfter = withSettings(maintenanceDraft);
        const dropped: { [key: string]: Attachment } = {};
        for (const componentKey of keys) {
            Object.assign(dropped, droppedAttachments(
                maintenanceDraft.components[componentKey]?.maintenanceActivities?.[templateId]?.costAttachments,
                draftAfter.components[componentKey]?.maintenanceActivities?.[templateId]?.costAttachments
            ));
        }
        tombstoneDropped('maintenanceActivityAttachment', dropped, draftAfter);

        setMaintenanceDraft(prev => {
            const updatedDraft = withSettings(prev);
            if (updatedDraft === prev) return prev;
            scheduleAutosave(updatedDraft);
            return updatedDraft;
        });
    }, [maintenanceDraft, setMaintenanceDraft, scheduleAutosave, tombstoneDropped]);

    const handleReplacementCostSettingsSave = useCallback((
        componentKeys: string[],
        settings: CostSettings
    ): void => {
        if (componentKeys.length === 0) return;
        const withSettings = (draft: MaintenanceDraftWithDone): MaintenanceDraftWithDone => {
            const components = { ...draft.components };
            for (const componentKey of componentKeys) {
                components[componentKey] = applyReplacementCostSettings(
                    components[componentKey] ?? { maintenanceActivities: {} },
                    settings
                );
            }
            return { ...draft, components };
        };

        const draftAfter = withSettings(maintenanceDraft);
        const dropped: { [key: string]: Attachment } = {};
        for (const componentKey of componentKeys) {
            Object.assign(dropped, droppedAttachments(
                maintenanceDraft.components[componentKey]?.replacementAttachments,
                draftAfter.components[componentKey]?.replacementAttachments
            ));
        }
        tombstoneDropped('maintenanceReplacementAttachment', dropped, draftAfter);

        setMaintenanceDraft(prev => {
            const updatedDraft = withSettings(prev);
            scheduleAutosave(updatedDraft);
            return updatedDraft;
        });
    }, [maintenanceDraft, setMaintenanceDraft, scheduleAutosave, tombstoneDropped]);

    const handleActivityCommentChange = useCallback((
        componentKey: string,
        templateId: string,
        text: string
    ): void => {
        setMaintenanceDraft(prev => {
            const existingActivity = prev.components[componentKey]?.maintenanceActivities?.[templateId];
            if (!existingActivity) return prev;

            const updatedActivity = withActivityComment(existingActivity, text);
            if (updatedActivity === existingActivity) return prev;

            const updatedDraft = {
                ...prev,
                components: {
                    ...prev.components,
                    [componentKey]: {
                        ...prev.components[componentKey],
                        maintenanceActivities: {
                            ...prev.components[componentKey]?.maintenanceActivities,
                            [templateId]: updatedActivity
                        }
                    }
                }
            };

            scheduleAutosave(updatedDraft);
            return updatedDraft;
        });
    }, [setMaintenanceDraft, scheduleAutosave]);

    // === Custom Quantity Handlers (Mängd for m²/lm, antal for kr/st) ===

    // Sets or (with undefined) removes one of the activity's calculation amounts.
    // Neither changes the component: they only feed the activity's cost.
    const setActivityAmount = useCallback((
        componentKey: string,
        templateId: string,
        field: 'customQuantity' | 'customUnitCount',
        value: number | undefined
    ): void => {
        setMaintenanceDraft(prev => {
            const existingActivity = prev.components[componentKey]?.maintenanceActivities?.[templateId];
            if (!existingActivity) return prev;

            const updatedActivity = { ...existingActivity, lastUpdated: formatDateForStorage(new Date()) };
            if (value === undefined) {
                delete updatedActivity[field];
            } else {
                updatedActivity[field] = value;
            }

            const updatedDraft = {
                ...prev,
                components: {
                    ...prev.components,
                    [componentKey]: {
                        ...prev.components[componentKey],
                        maintenanceActivities: {
                            ...prev.components[componentKey]?.maintenanceActivities,
                            [templateId]: updatedActivity
                        }
                    }
                }
            };

            scheduleAutosave(updatedDraft);
            return updatedDraft;
        });
    }, [setMaintenanceDraft, scheduleAutosave]);

    const handleActivityQuantityChange = useCallback((
        componentKey: string,
        templateId: string,
        quantity: number
    ): void => {
        // Validate: must be positive and within reasonable bounds
        if (quantity <= 0 || quantity > 1000000) return;
        setActivityAmount(componentKey, templateId, 'customQuantity', quantity);
    }, [setActivityAmount]);

    const handleActivityQuantityClear = useCallback((
        componentKey: string,
        templateId: string
    ): void => {
        setActivityAmount(componentKey, templateId, 'customQuantity', undefined);
    }, [setActivityAmount]);

    const handleActivityUnitCountChange = useCallback((
        componentKey: string,
        templateId: string,
        count: number
    ): void => {
        if (count <= 0 || count > 1000000) return;
        // Absent means 1, so 1 is stored as no antal
        setActivityAmount(componentKey, templateId, 'customUnitCount', count === 1 ? undefined : count);
    }, [setActivityAmount]);

    const handleActivityUnitCountClear = useCallback((
        componentKey: string,
        templateId: string
    ): void => {
        setActivityAmount(componentKey, templateId, 'customUnitCount', undefined);
    }, [setActivityAmount]);

    // Antal of a kr/st replacement: a cost multiplier only, never the component's count.
    // Absent means 1, so 1 (or a clear) removes it
    const setReplacementUnitCount = useCallback((componentKey: string, count: number | undefined): void => {
        setMaintenanceDraft(prev => {
            const existing = prev.components[componentKey] ?? { maintenanceActivities: {} };
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { replacementCustomUnitCount: _previous, ...rest } = existing;
            const updatedDraft = {
                ...prev,
                components: {
                    ...prev.components,
                    [componentKey]: count === undefined || count === 1 ? rest : { ...rest, replacementCustomUnitCount: count }
                }
            };

            scheduleAutosave(updatedDraft);
            return updatedDraft;
        });
    }, [setMaintenanceDraft, scheduleAutosave]);

    const handleReplacementUnitCountChange = useCallback((componentKey: string, count: number): void => {
        if (count <= 0 || count > 1000000) return;
        setReplacementUnitCount(componentKey, count);
    }, [setReplacementUnitCount]);

    const handleReplacementUnitCountClear = useCallback((componentKey: string): void => {
        setReplacementUnitCount(componentKey, undefined);
    }, [setReplacementUnitCount]);

    // === Bulk Apply Handler ===

    const handleBulkApply = useCallback((
        sourceKey: string,
        targetKeys: string[]
    ): void => {
        const sourceData = maintenanceDraft.components[sourceKey];
        if (!sourceData || targetKeys.length === 0) return;

        setMaintenanceDraft(prev => {
            const updatedComponents = { ...prev.components };

            for (const targetKey of targetKeys) {
                // Copy activities but strip customQuantity and customUnitCount (they're component-specific)
                const activitiesWithoutCustomQuantity: Record<string, unknown> = {};
                if (sourceData.maintenanceActivities) {
                    Object.entries(sourceData.maintenanceActivities).forEach(([templateId, activity]) => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { customQuantity, customUnitCount, ...activityWithoutQuantity } = activity;
                        activitiesWithoutCustomQuantity[templateId] = activityWithoutQuantity;
                    });
                }

                // Build settings object, only including defined values (Firebase rejects undefined)
                const settingsToCopy: Record<string, unknown> = {
                    // Replace all activities with source activities (excluding customQuantity and customUnitCount)
                    maintenanceActivities: activitiesWithoutCustomQuantity,
                };

                // Only copy defined values to avoid Firebase undefined errors
                if (sourceData.replacementYear !== undefined) {
                    settingsToCopy.replacementYear = sourceData.replacementYear;
                }
                if (sourceData.replacementCost !== undefined) {
                    settingsToCopy.replacementCost = sourceData.replacementCost;
                }
                if (sourceData.replacementCostSource !== undefined) {
                    settingsToCopy.replacementCostSource = sourceData.replacementCostSource;
                }
                if (sourceData.replacementCustomUnitPrice !== undefined) {
                    settingsToCopy.replacementCustomUnitPrice = sourceData.replacementCustomUnitPrice;
                }
                if (sourceData.replacementCostComment !== undefined) {
                    settingsToCopy.replacementCostComment = sourceData.replacementCostComment;
                }
                if (sourceData.replacementInvestmentPercentage !== undefined) {
                    settingsToCopy.replacementInvestmentPercentage = sourceData.replacementInvestmentPercentage;
                }
                if (sourceData.isDone !== undefined) {
                    settingsToCopy.isDone = sourceData.isDone;
                }
                if (sourceData.excluded !== undefined) {
                    settingsToCopy.excluded = sourceData.excluded;
                }

                // Removed rows follow the source (absent, not undefined, when it has none)
                const { removedActivities: targetRemoved, ...targetData } = updatedComponents[targetKey] ?? {};
                if (sourceData.removedActivities) {
                    settingsToCopy.removedActivities = sourceData.removedActivities;
                }

                updatedComponents[targetKey] = {
                    ...targetData,
                    ...settingsToCopy,
                } as typeof sourceData;
            }

            const updatedDraft = { ...prev, components: updatedComponents };
            scheduleAutosave(updatedDraft);
            return updatedDraft;
        });
    }, [maintenanceDraft, setMaintenanceDraft, scheduleAutosave]);

    // Replaces a component's whole activity set (Kopiera alla aktiviteter från annan komponent)
    const handleReplaceActivities = useCallback((
        componentKey: string,
        activities: { [templateId: string]: MaintenanceActivity },
        removedActivities: { [templateId: string]: true } | undefined
    ): void => {
        setMaintenanceDraft(prev => {
            const { removedActivities: previousRemoved, ...entry } = prev.components[componentKey] ?? {};
            const updatedDraft = {
                ...prev,
                components: {
                    ...prev.components,
                    [componentKey]: {
                        ...entry,
                        maintenanceActivities: activities,
                        // Absent, not undefined, when nothing is removed
                        ...(removedActivities ? { removedActivities } : {})
                    }
                }
            };

            scheduleAutosave(updatedDraft);
            return updatedDraft;
        });
    }, [setMaintenanceDraft, scheduleAutosave]);

    return {
        handleReplacementYearChange,
        handleReplacementYearBlur,
        handleReplacementCostChange,
        handleReplacementCostBlur,
        handleMaintenanceDateChange,
        handleMaintenanceDateBlur,
        handleMaintenanceIntervalChange,
        handleMaintenanceIntervalBlur,
        handleMaintenanceIntervalTypeChange,
        handleMaintenanceCostChange,
        handleMaintenanceCostBlur,
        handleMaintenanceActivityRemove,
        handleMaintenanceActivityAdd,
        handleDoneToggle,
        handleExcludeToggle,

        // Cost settings
        handleActivityCostSettingsSave,
        handleReplacementCostSettingsSave,
        handleActivityCommentChange,

        // Custom quantity (area/length override) and antal (per_unit)
        handleActivityQuantityChange,
        handleActivityQuantityClear,
        handleActivityUnitCountChange,
        handleActivityUnitCountClear,
        handleReplacementUnitCountChange,
        handleReplacementUnitCountClear,


        // Bulk apply
        handleBulkApply,
        handleReplaceActivities
    };
};
